import { store } from 'redux/store';
import { useTranslate } from 'hooks/useTranslate';
import { useDispatch, useSelector } from 'react-redux';
import { State } from 'redux/initialState';
import { Board, Document } from 'types';
import { FC, useState } from 'react';

import successSwal from 'utils/successSwal';
import errorSwal from 'utils/errorSwal';

import fileTypes from 'constants/fileTypes';
import documentTypes from 'constants/documentTypes';

import { setModal } from 'modules/_shared/redux/modalActions';
import { addDocument } from 'modules/documents/redux/documentActions';
import CustomLoading from 'modules/_shared/components/CustomLoading';

import boardTemplateTypes from 'modules/boards/constants/boardTemplateTypes';
import { updateBoard } from 'modules/boards/redux/boardActions';

import ReplaceTemplateWithDocumentModalHeader from './components/ReplaceTemplateWithDocumentModalHeader';
import ReplaceTemplateWithDocumentModalFooter from './components/ReplaceTemplateWithDocumentModalFooter';
import ReplaceTemplateWithDocumentModalBody from './components/ReplaceTemplateWithDocumentModalBody';

type ReplaceTemplateWithDocumentModalProps = {
  boardId: string;
  templateType: string;
};

const ReplaceTemplateWithDocumentModal: FC<
  ReplaceTemplateWithDocumentModalProps
> = ({ boardId, templateType }) => {
  const { t } = useTranslate();
  const dispatch = useDispatch();

  const user = useSelector((state: State) => state?.user);
  const board = useSelector((state: State) =>
    state?.boards.find((board: Board) => board._id === boardId)
  );

  const [selectedDocument, setSelectedDocument] = useState<string>();
  const [documentFile, setDocumentFile] = useState<File | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleCloseModal = () => {
    dispatch(setModal(null));
  };

  const handleChangeDocumentFile = (file: File) => {
    setDocumentFile(file);
    setSelectedDocument(undefined);
  };

  const handleChangeSelectedDocument = (selectedOption: any) => {
    setSelectedDocument(selectedOption.value);
    setDocumentFile(undefined);
  };

  const createDocumentData = () => ({
    file: documentFile,
    date: new Date(),
    size: documentFile?.size,
    name: documentFile?.name,
    author: user?._id,
    society: board?.society,
    fileType: fileTypes[documentFile?.type as keyof typeof fileTypes],
    category: documentTypes.DOCUMENTS,
    description: t('DocumentLinkedManually'),
  });

  const updateBoardDocument = async (uploadedDocument: Document | string) => {
    if (!board) return;

    const documentValue =
      typeof uploadedDocument === 'string'
        ? uploadedDocument
        : uploadedDocument._id;

    const documentKey =
      templateType === boardTemplateTypes.ACT
        ? 'actDocument'
        : 'actCertificateDocument';

    const boardData = {
      [documentKey]: documentValue,
    };

    await store.dispatch(updateBoard(board._id, boardData));
  };

  const handleChangeTemplate = async () => {
    try {
      setIsLoading(true);

      if (documentFile) {
        const documentData = createDocumentData();
        const uploadedDocument = await store.dispatch(
          addDocument(documentData)
        );
        await updateBoardDocument(uploadedDocument);
      } else if (selectedDocument) {
        await updateBoardDocument(selectedDocument);
      }

      successSwal(t('DocumentReplacedSuccesfully'));
    } catch (error) {
      errorSwal(t('ErrorReplacingDocument'));
    } finally {
      setIsLoading(false);
      handleCloseModal();
    }
  };

  return (
    <>
      <ReplaceTemplateWithDocumentModalHeader
        templateType={templateType}
        handleCloseModal={handleCloseModal}
      />

      <ReplaceTemplateWithDocumentModalBody
        board={board as Board}
        documentFile={documentFile}
        handleChangeSelectedDocument={handleChangeSelectedDocument}
        handleChangeDocumentFile={handleChangeDocumentFile}
      />

      <ReplaceTemplateWithDocumentModalFooter
        selectedTemplate={selectedDocument}
        documentFile={documentFile}
        handleChangeTemplate={handleChangeTemplate}
      />

      {isLoading && <CustomLoading />}
    </>
  );
};

export default ReplaceTemplateWithDocumentModal;
